import React, { useState, useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowUpRight, Check } from 'lucide-react';
import FlipButton from '../UI/FlipButton';
import Marquee from '../UI/Marquee';
import { LINKS } from '../../constants';
import { useLenis } from '../UI/SmoothScroll';

gsap.registerPlugin(useGSAP);

// Time the preloader holds the screen before the hero is revealed
export const INTRO_DELAY = 2.0;

// Words cycled inside the headline — display serif italics, one accent
const ROTATING = ['next', 'needed', 'missing', 'real'];

// Counted up on load — things I'm proud of, not job metrics
const HERO_STATS = [
  { prefix: '', target: 3, suffix: '', label: 'Tools shipped & adopted' },
  { prefix: '', target: 6, suffix: '×', label: 'Faster reporting, built myself' },
  { prefix: '', target: 5, suffix: '+', label: 'Certifications, JPMC to Google' },
  { prefix: '', target: 3, suffix: '', label: 'Languages spoken fluently' },
];

// Floating micro-chips around the headline (desktop only)
const FLOATERS = [
  { text: 'Finance × Technology', pos: 'left-[5%] top-[23%]', depth: 28, rot: '-rotate-6' },
  { text: 'Self-taught builder', pos: 'right-[5%] top-[20%]', depth: 38, rot: 'rotate-3' },
  { text: 'Curious by default', pos: 'left-[7%] bottom-[31%]', depth: 44, rot: 'rotate-2' },
  { text: 'GenAI power user', pos: 'right-[6%] bottom-[28%]', depth: 22, rot: '-rotate-3' },
];

const MARQUEE_ITEMS = [
  'Finance × Technology',
  'Always Learning',
  'Data Visualization',
  'Financial Modeling',
  'Process Automation',
  'GenAI Prototyping',
  'Vibe Coding',
  'Excel · VBA · SPSS',
  'Storytelling with Data',
  'English · Hindi · Bengali',
];

// Split a line into per-word mask spans for the intro timeline
const Line: React.FC<{ text: string; className?: string }> = ({ text, className }) => (
  <span className={`block ${className ?? ''}`}>
    {text.split(' ').map((word, i) => (
      <React.Fragment key={i}>
        <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
          <span className='hero-word inline-block will-change-transform'>{word}</span>
        </span>
        {i < text.split(' ').length - 1 ? ' ' : ''}
      </React.Fragment>
    ))}
  </span>
);

// Button wrapper that leans toward the cursor
const Magnetic: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      if (
        window.matchMedia('(pointer: coarse)').matches ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        return;

      const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3' });

      const move = (e: MouseEvent) => {
        const r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.3);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.3);
      };
      const leave = () => {
        xTo(0);
        yTo(0);
      };

      el.addEventListener('mousemove', move);
      el.addEventListener('mouseleave', leave);
      return () => {
        el.removeEventListener('mousemove', move);
        el.removeEventListener('mouseleave', leave);
      };
    },
    { scope: ref }
  );

  return (
    <div ref={ref} className='inline-block will-change-transform'>
      {children}
    </div>
  );
};

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const rotWrapRef = useRef<HTMLSpanElement>(null);
  const [copied, setCopied] = useState(false);
  const { scrollTo } = useLenis();

  useGSAP(
    () => {
      const words = gsap.utils.toArray<HTMLElement>('.rot-word');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (words[0]) gsap.set(words[0], { yPercent: 0, autoAlpha: 1 });
        gsap.set('.hero-float', { autoAlpha: 1 });
        return;
      }

      // ---- Rotating headline word setup ----
      let current = 0;
      gsap.set(words, { yPercent: 120, autoAlpha: 1 });
      const remeasure = () => {
        if (rotWrapRef.current && words[current]) {
          gsap.set(rotWrapRef.current, { width: words[current].offsetWidth });
        }
      };
      remeasure();
      document.fonts?.ready.then(remeasure);
      document.fonts?.addEventListener('loadingdone', remeasure);
      window.addEventListener('resize', remeasure);

      // ---- Intro timeline (starts after the preloader curtain lifts) ----
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' }, delay: INTRO_DELAY });
      tl.fromTo(
        '.hero-word',
        { yPercent: 115 },
        { yPercent: 0, duration: 1.05, stagger: 0.05 },
        0.15
      )
        .to(words[0], { yPercent: 0, duration: 0.9 }, 0.45)
        .fromTo(
          '.hero-fade',
          { opacity: 0, y: 18 },
          { opacity: 1, y: 0, duration: 0.8, stagger: 0.09 },
          0.7
        )
        .fromTo(
          '.hero-float',
          { autoAlpha: 0, scale: 0.7, y: 14 },
          { autoAlpha: 1, scale: 1, y: 0, duration: 0.7, stagger: 0.08, ease: 'back.out(1.6)' },
          1.0
        );

      // ---- Stat counters ----
      gsap.utils.toArray<HTMLElement>('.hero-count').forEach(el => {
        const target = parseFloat(el.dataset.target || '0');
        const state = { v: 0 };
        el.textContent = '0';
        gsap.to(state, {
          v: target,
          duration: 1.6,
          ease: 'power3.out',
          delay: INTRO_DELAY + 1.1,
          onUpdate: () => {
            el.textContent = String(Math.round(state.v));
          },
        });
      });

      // ---- Headline word rotation loop ----
      const HOLD = 2.4;
      const swap = () => {
        const cur = words[current];
        current = (current + 1) % words.length;
        const next = words[current];
        gsap.to(cur, { yPercent: -120, duration: 0.5, ease: 'power3.in' });
        gsap.fromTo(
          next,
          { yPercent: 120 },
          { yPercent: 0, duration: 0.6, ease: 'power3.out', delay: 0.12 }
        );
        if (rotWrapRef.current) {
          gsap.to(rotWrapRef.current, {
            width: next.offsetWidth,
            duration: 0.45,
            ease: 'power3.inOut',
            delay: 0.05,
          });
        }
        gsap.delayedCall(HOLD, swap);
      };
      gsap.delayedCall(INTRO_DELAY + HOLD + 1.2, swap);

      // ---- Idle bob on the floating chips ----
      gsap.utils.toArray<HTMLElement>('.hero-float-bob').forEach((el, i) => {
        gsap.to(el, {
          y: i % 2 ? 12 : -12,
          duration: 2.2 + i * 0.45,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: i * 0.25,
        });
      });

      // ---- Drifting color glows ----
      gsap.to('.hero-glow-a', {
        x: 60,
        y: 40,
        duration: 9,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
      gsap.to('.hero-glow-b', {
        x: -50,
        y: -30,
        duration: 11,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      // ---- Mouse parallax on floaters ----
      const cleanups: (() => void)[] = [
        () => {
          document.fonts?.removeEventListener('loadingdone', remeasure);
          window.removeEventListener('resize', remeasure);
        },
      ];
      if (window.matchMedia('(pointer: fine)').matches && sectionRef.current) {
        const node = sectionRef.current;
        const fns = gsap.utils.toArray<HTMLElement>('.hero-float').map(el => ({
          x: gsap.quickTo(el, 'x', { duration: 0.9, ease: 'power3' }),
          y: gsap.quickTo(el, 'y', { duration: 0.9, ease: 'power3' }),
          d: parseFloat(el.dataset.depth || '24'),
        }));
        const onMove = (e: MouseEvent) => {
          const nx = e.clientX / window.innerWidth - 0.5;
          const ny = e.clientY / window.innerHeight - 0.5;
          fns.forEach(f => {
            f.x(nx * f.d);
            f.y(ny * f.d);
          });
        };
        node.addEventListener('mousemove', onMove);
        cleanups.push(() => node.removeEventListener('mousemove', onMove));
      }
      return () => cleanups.forEach(fn => fn());
    },
    { scope: sectionRef }
  );

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(LINKS.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.location.href = `mailto:${LINKS.email}`;
    }
  };

  return (
    <section ref={sectionRef} id='intro' className='relative bg-paper overflow-hidden'>
      {/* Viewport lockup: glows + floaters + headline. Stats live below the fold. */}
      <div className='relative'>
      {/* Drifting color glows — colors live in index.css (dimmer in dark mode) */}
      <div
        aria-hidden='true'
        className='hero-glow-a pointer-events-none absolute -top-32 -left-32 w-[34rem] h-[34rem] rounded-full blur-3xl'
      />
      <div
        aria-hidden='true'
        className='hero-glow-b pointer-events-none absolute -bottom-40 -right-32 w-[38rem] h-[38rem] rounded-full blur-3xl'
      />

      {/* Floating micro-chips */}
      {FLOATERS.map(f => (
        <div
          key={f.text}
          data-depth={f.depth}
          className={`hero-float hidden lg:block absolute ${f.pos} z-10 opacity-0 will-change-transform`}
        >
          <span
            className={`hero-float-bob label-mono flex items-center gap-2 px-3.5 py-2 rounded-full border border-line bg-paper/80 backdrop-blur-sm text-gray1 shadow-sm ${f.rot}`}
          >
            <span className='h-1.5 w-1.5 rounded-full bg-accent' />
            {f.text}
          </span>
        </div>
      ))}

      {/* Full-viewport lockup — the stats strip lives below the fold */}
      <div className='min-h-[100svh] flex flex-col items-center justify-center text-center max-w-6xl w-full mx-auto px-5 sm:px-6 pt-28 pb-16'>
        {/* Mono status label */}
        <div className='hero-fade label-mono text-gray1 mb-8 inline-flex items-start gap-2.5 text-left'>
          <span className='relative flex h-1.5 w-1.5 mt-1 shrink-0'>
            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-60' />
            <span className='relative inline-flex rounded-full h-1.5 w-1.5 bg-accent' />
          </span>
          Open to opportunities
        </div>

        {/* Giant heading: small kicker → the name (focal point) → rotating tagline */}
        <h1 className='font-medium tracking-tight leading-[1.08]'>
          {/* Kicker — small, muted */}
          <span className='block font-serif italic text-gray1 text-[clamp(1.1rem,2.3vw,1.75rem)] mb-3 sm:mb-4'>
            <Line text='Finance mind.' />
          </span>
          {/* The name — the largest element on the page */}
          <span className='block font-display text-ink text-[clamp(3rem,13vw,8.5rem)] leading-[1.02]'>
            <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
              <span className='hero-word inline-block will-change-transform'>Akash&nbsp;</span>
            </span>
            <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
              <span className='hero-word inline-block will-change-transform'>Roy</span>
            </span>
            <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
              <span className='hero-word inline-block will-change-transform text-accent'>.</span>
            </span>
          </span>
          {/* Tagline — secondary, with the rotating word */}
          <span className='block text-gray1 text-[clamp(1.35rem,4.6vw,3rem)] leading-[1.1] mt-4 sm:mt-5'>
            <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
              <span className='hero-word inline-block will-change-transform'>
                Building what&apos;s&nbsp;
              </span>
            </span>
            <span
              ref={rotWrapRef}
              className='relative inline-block overflow-hidden align-bottom whitespace-nowrap pb-[0.12em] -mb-[0.12em]'
            >
              <span className='invisible inline-block font-display pr-[0.08em]'>
                {ROTATING[0]}
              </span>
              {ROTATING.map(word => (
                <span
                  key={word}
                  className='rot-word absolute left-0 top-0 inline-block opacity-0 will-change-transform font-display text-accent pr-[0.08em]'
                >
                  {word}
                </span>
              ))}
            </span>
            <span className='inline-block overflow-hidden align-bottom pb-[0.12em] -mb-[0.12em]'>
              <span className='hero-word inline-block will-change-transform text-gray2'>.</span>
            </span>
          </span>
        </h1>

        {/* Bio */}
        <div className='hero-fade mt-8 max-w-2xl text-center'>
          <p className='text-base sm:text-lg leading-relaxed text-gray1'>
            Finance gave me the discipline; teaching myself to code gave me the tools. Now I turn
            slow, manual workflows into{' '}
            <span className='text-accent font-medium'>software people actually use</span>.
            Self-taught, endlessly curious, and just getting started.
          </p>
        </div>

        {/* CTAs */}
        <div className='hero-fade mt-10 flex flex-wrap items-center justify-center gap-3'>
          <Magnetic>
            <FlipButton label='See Work' onClick={() => scrollTo('#projects', -72)} variant='dark' />
          </Magnetic>
          <Magnetic>
            <FlipButton
              label={copied ? 'Email copied' : 'Copy email'}
              onClick={copyEmail}
              variant='outline'
              className='text-ink'
              icon={copied ? <Check size={15} className='text-accent' /> : undefined}
            />
          </Magnetic>
          <Magnetic>
            <FlipButton
              label='Résumé'
              href={LINKS.resume}
              variant='outline'
              className='text-ink'
              icon={<ArrowUpRight size={15} className='text-accent' />}
            />
          </Magnetic>
        </div>

      </div>
      </div>

      {/* Counted stats strip */}
      <div className='border-t border-line'>
        <div className='max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4'>
          {HERO_STATS.map((s, i) => (
            <div
              key={s.label}
              className={`hero-fade py-5 sm:py-6 px-3 sm:px-6 text-center border-line ${i % 2 !== 0 ? 'border-l' : ''} ${i >= 2 ? 'border-t lg:border-t-0' : ''} ${i >= 2 && i % 2 === 0 ? 'lg:border-l' : ''} ${i === 1 ? 'lg:border-l' : ''}`}
            >
              <div className='font-medium tracking-tight text-2xl sm:text-3xl text-ink'>
                {s.prefix}
                <span className='hero-count' data-target={s.target}>
                  {s.target}
                </span>
                {s.suffix}
              </div>
              <div className='label-mono text-gray2 mt-1.5'>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee strip */}
      <div className='border-t border-line py-4'>
        <Marquee
          items={MARQUEE_ITEMS}
          className='label-mono !text-xs text-gray1'
          separator={<span className='mx-6 text-accent select-none'>✦</span>}
        />
      </div>
    </section>
  );
};

export default Hero;
